import type { CustomerRow } from "@/types/admin";

export interface OrgFilters {
  search: string;
  plans: Set<string>;
  statuses: Set<string>;
  view: "active" | "archived";
}

/**
 * Drop the flagged demo tenant(s) (shield-control#238) and our own internal orgs (shield-control#843) — used wherever the demo
 * org must not pollute internal aggregates or selection targets (dashboard
 * counts, invite/signup-code/command-search dropdowns). The customers table keeps
 * them (badged) so staff can still see the demo org.
 */
export function realOrgs(rows: CustomerRow[]): CustomerRow[] {
  return rows.filter((r) => !r.is_demo && !r.is_internal);
}

export function filterOrgs(rows: CustomerRow[], f: OrgFilters): CustomerRow[] {
  const q = f.search.trim().toLowerCase();
  return rows.filter((r) => {
    if (f.view === "active" ? r.deleted_at !== null : r.deleted_at === null) return false;
    if (q) {
      const hay = `${r.name} ${r.domain ?? ""} ${r.industry ?? ""}`.toLowerCase();
      if (!hay.includes(q)) return false;
    }
    if (f.plans.size > 0 && !f.plans.has(r.plan ?? "")) return false;
    if (f.statuses.size > 0 && !f.statuses.has(r.subscription_status ?? "")) return false;
    return true;
  });
}

/** Generic column sort: numbers numeric, strings case-insensitive, nulls always last. */
export function sortRows<T extends object>(rows: T[], column: string, direction: "ascending" | "descending"): T[] {
  const dir = direction === "ascending" ? 1 : -1;
  return rows.toSorted((a, b) => {
    const av = (a as Record<string, unknown>)[column];
    const bv = (b as Record<string, unknown>)[column];
    if (av == null && bv == null) return 0;
    if (av == null) return 1;
    if (bv == null) return -1;
    if (typeof av === "number" && typeof bv === "number") return (av - bv) * dir;
    return String(av).localeCompare(String(bv), undefined, { sensitivity: "base" }) * dir;
  });
}
